import { notification } from '@/shared/messaging';
import { cookieStorageService } from '@/shared/services/cookieStorageService';
import { localStorageService } from '@/shared/services/localStorageService';
import axios, { AxiosInstance } from 'axios';

export enum HttpMethods {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  PATCH = 'PATCH',
  DELETE = 'DELETE',
}

// Mutex in-tab para evitar multiples refreshes simultaneos dentro de una pestana
let isRefreshing = false;
let refreshSubscribers: ((token: string | null) => void)[] = [];

function onRefreshed(token: string | null) {
  const subs = [...refreshSubscribers];
  refreshSubscribers = [];
  subs.forEach((cb) => cb(token));
}

function addRefreshSubscriber(cb: (token: string | null) => void) {
  refreshSubscribers.push(cb);
}

// Snapshot del accessToken ANTES de intentar refresh.
// Usado por check-after-lock para detectar si otra tab ya renovo.
let preRefreshAccessToken: string | null = null;

/**
 * Ejecuta el POST de refresh real contra el backend.
 * Solo debe llamarse dentro del lock cross-tab.
 */
async function doRefreshPost(): Promise<string | null> {
  const storedRefreshToken = cookieStorageService.getItem('refreshToken');
  if (!storedRefreshToken) return null;

  const baseUrl = `${import.meta.env.VITE_API_BASE_URL}/api`;
  const refreshTokenValue = JSON.parse(storedRefreshToken as string);

  const refreshResponse = await axios.post(`${baseUrl}/Autenticacion/RenovarSesion`, {
    refreshToken: refreshTokenValue,
  });

  const responseData = refreshResponse.data;
  if (responseData?.success && responseData?.dataResponse) {
    const newAccessToken = responseData.dataResponse.token;
    const newRefreshToken = responseData.dataResponse.refreshToken;
    const newSessionId = responseData.dataResponse.sessionId;

    cookieStorageService.setItem('accessToken', JSON.stringify(newAccessToken));
    if (newRefreshToken) {
      cookieStorageService.setItem('refreshToken', JSON.stringify(newRefreshToken));
    }
    if (newSessionId) {
      cookieStorageService.setItem('sessionId', newSessionId);
    }
    return newAccessToken;
  }
  return null;
}

/**
 * Funcion centralizada de refresh con:
 * - Mutex in-tab (isRefreshing + subscribers) para coalescer peticiones dentro de 1 tab.
 * - Lock cross-tab (navigator.locks) para que solo 1 tab haga el POST a la vez.
 * - Check-after-lock: si otra tab ya renovo mientras esperabamos el lock, usamos ese token.
 */
export function refreshAccessToken(): Promise<string | null> {
  // 1. Mutex in-tab: si ya hay un refresh en curso en ESTA tab, esperar su resultado
  if (isRefreshing) {
    return new Promise((resolve) => {
      addRefreshSubscriber((newToken: string | null) => {
        resolve(newToken);
      });
    });
  }

  isRefreshing = true;

  // Guardar snapshot del accessToken actual para check-after-lock
  preRefreshAccessToken = (cookieStorageService.getItem('accessToken') as string) ?? null;

  const storedRefreshToken = cookieStorageService.getItem('refreshToken');
  if (!storedRefreshToken) {
    isRefreshing = false;
    onRefreshed(null);
    return Promise.resolve(null);
  }

  // 2. Lock cross-tab: adquirir lock ANTES de hacer el POST
  const doLockedRefresh = async (): Promise<string | null> => {
    // Check-after-lock: si el accessToken cambio mientras esperabamos el lock,
    // otra tab ya renovo exitosamente. Usar ese token sin hacer POST.
    const currentAccessToken = (cookieStorageService.getItem('accessToken') as string) ?? null;
    if (currentAccessToken && currentAccessToken !== preRefreshAccessToken) {
      return JSON.parse(currentAccessToken) as string;
    }

    // Nadie renovo todavia: hacer el POST real
    try {
      return await doRefreshPost();
    } catch {
      return null;
    }
  };

  const hasNavigatorLocks = typeof navigator !== 'undefined' && navigator.locks && typeof navigator.locks.request === 'function';

  // navigator.locks.request awaits el callback y retorna su resultado.
  // TypeScript lib.dom declara retorno como Promise<any>, cast necesario.
  const refreshPromise: Promise<string | null> = hasNavigatorLocks ? (navigator.locks.request('erp_refresh_token_lock', doLockedRefresh) as unknown as Promise<string | null>) : doLockedRefresh();

  return refreshPromise
    .then((newAccessToken: string | null) => {
      onRefreshed(newAccessToken);
      return newAccessToken;
    })
    .catch(() => {
      onRefreshed(null);
      return null;
    })
    .finally(() => {
      // Safety net: garantizar cleanup SIEMPRE, incluso ante errores sincronos inesperados
      isRefreshing = false;
    });
}

function clearSessionAndRedirect() {
  cookieStorageService.removeItem('accessToken');
  cookieStorageService.removeItem('refreshToken');
  cookieStorageService.removeItem('sessionId');
  localStorageService.removeItem('current_user');
  localStorageService.removeItem('menu');
  localStorageService.removeItem('fcm_token');
  window.location.href = '/login';
}

export class AxiosClient {
  private baseUrl: string;
  private instance: AxiosInstance;

  constructor() {
    this.baseUrl = `${import.meta.env.VITE_API_BASE_URL}/api`;
    this.instance = axios.create({ baseURL: this.baseUrl, withCredentials: false });
  }

  get defaultHeaders() {
    const json = cookieStorageService?.getItem('accessToken');
    const token = json != undefined ? (JSON.parse(json) ?? '') : '';

    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  public request<T>(method: string, url: string, data: unknown = null, customHeaders = {}, options = {}): Promise<T> {
    const headers = { ...this.defaultHeaders, ...customHeaders };
    const controller = new AbortController();

    const config = {
      method,
      url,
      headers,
      signal: controller.signal,
      data: null,
      ...options,
    };

    if (data) {
      Object.assign(config, { data: data });
    }

    return new Promise((resolve, reject) => {
      this.instance(config)
        .then((response) => {
          resolve(response as T);
        })
        .catch((error) => {
          if (axios.isCancel(error)) {
            reject(error);
            return;
          } else {
            if (error?.response?.status === 401) {
              // No intentar refresh para el endpoint de renovar (evitar loop)
              if (url.includes('/RenovarSesion')) {
                notification.error({ message: 'Sesión expirada, inicie sesión de nuevo' });
                clearSessionAndRedirect();
                reject(error.message);
                return;
              }

              const storedRefreshToken = cookieStorageService.getItem('refreshToken');
              if (!storedRefreshToken) {
                notification.error({ message: 'El token expiró, inicie sesión de nuevo' });
                clearSessionAndRedirect();
                reject(error.message);
                return;
              }

              // Usar función centralizada de refresh (compartida con useRefreshToken)
              refreshAccessToken()
                .then((newAccessToken) => {
                  if (newAccessToken) {
                    const retryHeaders = {
                      ...headers,
                      Authorization: `Bearer ${newAccessToken}`,
                    };
                    const { signal: _signal, ...retryConfig } = config;
                    void _signal;
                    this.instance({ ...retryConfig, headers: retryHeaders })
                      .then((response) => resolve(response as T))
                      .catch((retryError) => reject(retryError));
                  } else {
                    notification.error({ message: 'Sesión expirada, inicie sesión de nuevo' });
                    clearSessionAndRedirect();
                    reject(error.message);
                  }
                })
                .catch(() => {
                  notification.error({ message: 'Sesión expirada, inicie sesión de nuevo' });
                  clearSessionAndRedirect();
                  reject(error.message);
                });
            } else if (error?.response?.status === 402) {
              // Manejo de suscripción inactiva
              import('@/app/subscripcion/store/subscriptionGuardStore').then(({ useSubscriptionGuardStore }) => {
                useSubscriptionGuardStore.getState().setBlocked(true, error?.response?.data?.ResponseMessage ?? 'Su suscripción ha expirado.');
              });
              reject(error?.response?.data?.ResponseMessage ?? 'Suscripción inactiva');
              return;
            } else if (error?.response?.status === 409 && error?.response?.data?.status === 'provisioning_pending') {
              import('@/app/subscripcion/store/subscriptionGuardStore').then(({ useSubscriptionGuardStore }) => {
                useSubscriptionGuardStore.getState().setProvisioning(true);
              });
              reject(error?.response?.data?.message ?? 'Cuenta en configuración');
              return;
            } else if (error?.response?.status != 200) {
              // El backend envía responseMessage (minúscula). Mantener ResponseMessage
              // como fallback temporal hasta confirmar que ningún endpoint use la forma vieja.
              const backendMessage = error?.response?.data?.responseMessage ?? error?.response?.data?.ResponseMessage;
              if (error.message === 'Network Error' && !error.response) {
                notification.error({
                  message: 'Sin conexión al servidor',
                });
                reject(error.message);
              } else if (error?.response?.status == 400) {
                if (error?.response?.data?.errors) {
                  for (const value in error?.response?.data?.errors) {
                    notification.warning({
                      message: error?.response?.data?.errors[value] ?? 'Ocurrió un error inesperado',
                    });
                  }
                  reject(error?.response?.data?.errors);
                } else {
                  // FIX: Manejar errores 400 sin formato errors (evita promesas colgadas)
                  notification.warning({
                    message: backendMessage ?? 'Error de validación',
                  });
                  reject(backendMessage ?? 'Error 400');
                }
              } else if (error?.response?.status == 403) {
                // 403 = autorización esperable (tenant virgen, permiso faltante). No
                // disparar toast global; dejar que React Query / el componente decida.
                reject(backendMessage ?? 'No autorizado');
              } else {
                notification.error({
                  message: backendMessage ?? 'Ocurrió un error inesperado',
                });
                reject(backendMessage);
              }
            } else {
              reject(error);
            }
          }
        });
    });
  }
}
